




























































import { Vue, Component, Prop, Watch } from "@/shared/vuepack";
import { FetchState } from "@/shared/types";
import { inRange } from "lodash";
import moment from "moment";
import Widget from "@/shared/components/Widget.vue";
import Button from "@/shared/components/ui/Button.vue";
import Input from "@/shared/components/ui/Input.vue";
import ContributionsNavigation from "@/employer/components/profile/ContributionsNavigation.vue";
import YearEdit from "@/employer/components/payrollcalendar/YearEdit.vue";
import PayrollEditSummaryModal from "@/employer/components/payrollcalendar/PayrollEditSummaryModal.vue";
import BeforeAbandonEditsModal from "@/employer/components/payrollcalendar/BeforeAbandonEditsModal.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";
import { useProfileStore } from "@/employer/store/profile";
import { usePayrollCalendarStore } from "@/employer/store/payrollCalendar";

@Component({
  components: {
    Button,
    Input,
    Widget,
    ContributionsNavigation,
    YearEdit,
    PayrollEditSummaryModal,
    BeforeAbandonEditsModal
  }
})
export default class PayrollCalendar extends Vue {

  userStore = useUserStore();
  profileStore = useProfileStore();
  payrollCalendarStore = usePayrollCalendarStore();
  notifierStore = useNotifierStore();

  showValidation = false;
  showUnsavedChanges = false;
  isNavigatingAway = false;

  year = moment().year();

  editMode = false;

  get employerId () { return this.forEmployerId || this.userStore.employerId; }

  @Prop({ required: false })
  forEmployerId: number;

  @Prop({ required: false })
  hideHeader: boolean;

  @Prop({ required: false })
  payrollHistoryView: boolean;

  isSubmitting = false;

  leavePage: Promise<boolean> = null;
  leavePageResolve: (resolveWithValue: boolean) => void;

  tryLeavePageResolve (resolveWithValue: boolean) {
    if (this.isNavigatingAway) {
      this.leavePageResolve(resolveWithValue);
    }
  }

  @Watch("employerId")
  onEmployerIdChanged () {
    this.profileStore.getPayrollInformation(this.employerId);
  }

  get isLoaded () {
    return this.profileStore.payrollInformationState === FetchState.Loaded
      && this.profileStore.payrollInformation.employerId === this.employerId;
  }

  get firstEligibleDate () {
    if (this.year === moment(this.profileStore.payrollInformation.firstDateOfYear).year()) {
      return this.profileStore.payrollInformation.firstDateOfYear;
    }
    return null;
  }

  get lockEndDate () {
    return this.profileStore.payrollInformation.payrollCalendarLockEndDate;
  }

  enableYear (proposedYear: number) {
    return inRange(proposedYear,
      moment(this.profileStore.payrollInformation.firstDateOfYear).year(),
      2 + moment().year());
  }

  mounted (): void {
    this.onEmployerIdChanged();
    window.addEventListener("beforeunload", this.onBeforeUnload);
  }

  beforeDestroy () {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  get isSaveDisabled () {
    return this.isSubmitting || !this.editMode;
  }

  get disabledCalendarEdits () {
    return this.isSubmitting || !this.editMode;
  }

  get isLockEndDateTheSentinelValue () {
    const lockEndMoment = moment(this.lockEndDate);
    return lockEndMoment.year() === 2030 && lockEndMoment.month() === 0 && lockEndMoment.date() === 1;
  }

  get showSaveNextButton (): boolean {
    return !this.isLockEndDateTheSentinelValue && !this.payrollHistoryView;
  }

  onSubmit (): void {
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.payrollCalendarStore.saveAllPayrollDates()
      .then(_ => {
        this.notifierStore.setSuccess("Saved");
        this.editMode = false;
      })
      .finally(() => {
        this.isSubmitting = false;
        this.showValidation = false;
        this.showUnsavedChanges = false;
        this.tryLeavePageResolve(true);
      });
  }

  onValidate () {
    this.showValidation = true;
    this.payrollCalendarStore.validateAllPayrollDates();
  }

  onValidationCancel () {
    this.showValidation = false;
    this.showUnsavedChanges = false;
    this.editMode = true; // ensure they are still in edit mode if they tried to leave it then closed the modal
    this.tryLeavePageResolve(false);
    this.leavePage = null;
  }

  onRevert () {
    this.showUnsavedChanges = false;
    this.payrollCalendarStore.revertAllChanges();
    this.tryLeavePageResolve(true);
  }

  @Watch("editMode")
  onEditModeChanged () {
    if (!this.editMode && this.payrollCalendarStore.isAnyYearDirty) {
      this.isNavigatingAway = false;
      this.showUnsavedChanges = true;
      this.payrollCalendarStore.validateAllPayrollDates();
    }
  }

  beforeRouteLeave (to, from, next) {
    this.payrollCalendarStore.validateAllPayrollDates();
    this.isNavigatingAway = true;
    this.showUnsavedChanges = true;

    if (this.leavePage === null) {
      // the first time here
      this.leavePage = new Promise((res, rej) => {
        this.leavePageResolve = res;
      });
      this.leavePage.then(val => {
        if (val) {
          next();
        } else {
          next(false);
          this.isNavigatingAway = false;
        }
        this.leavePage = null;
      });
    } else {
      next(false);
    }
  }

  onBeforeUnload (event: BeforeUnloadEvent) {
    if (!this.editMode || !this.payrollCalendarStore.isAnyYearDirty) {
      return;
    }

    event.returnValue = "You have unsaved changes to your payroll calendar.";
  }
}
